import { VALIDATION_STATUS } from "./types";

/**
 * Build a normalized test result from errors, warnings, successes, or skip.
 * @param {Object} opts
 * @param {boolean} [opts.skip]
 * @param {string} [opts.skipReason]
 * @param {Array<string|{message: string, developerNotes?: string}>} [opts.errors]
 * @param {Array<string|{message: string, developerNotes?: string}>} [opts.warnings]
 * @param {Array<string|{message: string, developerNotes?: string}>} [opts.successes]
 * @param {string} [opts.developerNotes]
 */
export function buildResult({
  skip = false,
  skipReason = "Not applicable for this patient",
  errors = [],
  warnings = [],
  successes = [],
  developerNotes,
} = {}) {
  if (skip) {
    return {
      status: VALIDATION_STATUS.SKIP,
      messages: [{ level: "warning", message: skipReason }],
      developerNotes,
    };
  }

  const toMessage = (item, level) => {
    if (typeof item === "string") return { level, message: item };
    return { level, message: item.message, developerNotes: item.developerNotes };
  };

  const messages = [
    ...errors.map((e) => toMessage(e, VALIDATION_STATUS.ERROR)),
    ...warnings.map((w) => toMessage(w, VALIDATION_STATUS.WARNING)),
    ...successes.map((s) => toMessage(s, VALIDATION_STATUS.SUCCESS)),
  ];

  let status = VALIDATION_STATUS.SUCCESS;
  if (errors.length > 0) status = VALIDATION_STATUS.ERROR;
  else if (warnings.length > 0) status = VALIDATION_STATUS.WARNING;

  return { status, messages, developerNotes };
}

export function skip(reason, developerNotes) {
  return buildResult({ skip: true, skipReason: reason, developerNotes });
}

export function pass(message, developerNotes) {
  return buildResult({
    successes: message ? [message] : ["Check passed"],
    developerNotes,
  });
}

export function fail(errors, developerNotes) {
  const list = Array.isArray(errors) ? errors : [errors];
  return buildResult({ errors: list, developerNotes });
}
